import type { LessonCreated, LessonReschedulePayload } from '../../model/lessons/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface LessonRescheduleApiOptions {
  disciplineId: string | number
  lessonId: number
  payload: LessonReschedulePayload
  onStart?: () => void
  onFinish?: () => void
}

/** `POST /admin-api/disciplines/{discipline}/lessons/{booking}/reschedule` — closes the lesson and creates its
 *  replacement; the answer is the replacement. Nothing is half-done on a refusal. */
export const useLessonRescheduleApi = async (options: LessonRescheduleApiOptions): Promise<LessonCreated | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.lessons.api.reschedule', {
    discipline_id: options.disciplineId,
    lesson_id: options.lessonId,
  })

  options.onStart?.()

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.post<ApiResponse>(url, options.payload)
    const replacement: LessonCreated = data.data

    toast.success({
      title: 'Successfully rescheduled!',
      description: `Selected lesson was successfully rescheduled! New lesson #${replacement.id}.`,
    })

    return replacement
  } catch (err: unknown) {
    toast.error({
      title: 'Error on reschedule!',
      description: getAxiosErrorMessage(err),
    })

    return null
  } finally {
    options.onFinish?.()
  }
}
